import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';

import { instanceOfDOMNodeOrNull } from 'bloko/common/helpers/customPropTypes';
import { LAYERS, LAYER_CLASSES, DISTANCE } from 'bloko/blocks/drop/constants';
import defaultProps, {
    THEMES,
    TIP_PLACEMENTS,
    PLACEMENT_TIP_SEQUENCE,
    BASE_CLASS_NAMES,
    ARROW_SIZE,
    ANIMATION_TIMEOUT_MS,
} from 'bloko/blocks/drop/Tip/constants';
import { updatePosition, setInitialCSSMetrics, getThemeClass } from 'bloko/blocks/drop/common';
import { Context } from './Context';

import './tip.less';

const Tip = (props) => <Context.Consumer>{(value) => <TipComponent {...props} context={value} />}</Context.Consumer>;

Tip.layers = LAYERS;
Tip.placements = TIP_PLACEMENTS;
Tip.themes = THEMES;

Tip.defaultProps = defaultProps;

Tip.propTypes = {
    /** Предпочтительное положение дроптипа, доступны в статическом свойстве [placements](#tip-placements).
     * Если в указанном направлении недостаточно места для отображения, подсказка будет показана в
     * направлении, которое больше подходит. */
    placement: PropTypes.oneOf(Object.values(TIP_PLACEMENTS)),
    /** Тип слоя z-index-а подсказки, доступны в статическом свойстве [layers](#tip-layers) */
    layer: PropTypes.oneOf(Object.values(LAYERS)),
    /** Возможные темы дроптипа доступны в статическом свойстве [themes](#tip-themes)*/
    theme: PropTypes.oneOf(Object.values(Tip.themes)),
    /** DOM нода хоста в рамках которого нужно рендерить дроптип, по дефолту рендер будет в родителе компонента.*/
    host: instanceOfDOMNodeOrNull(),
    /** Флаг отвечает за показ дроптипа, `true` - показать, `false` - скрыть */
    show: PropTypes.bool,
    /** Элемент инициатор, относительно которого показывать всплывающую подсказку */
    children: PropTypes.node,
    /** Метод для рендера данных внутри типа */
    render: PropTypes.func,
    /** Колбек вызывается, когда показан другой дроптип. Это связано с тем что дроптип может быть показан только 1
     * в момент времени, и нужно корректно обрабатывать state родителя, который отвечает за показ дроптипа */
    onExternalClose: PropTypes.func.isRequired,
    /* @param {String} dataQa='bloko-drop-tip' Data-qa активного info */
    dataQa: PropTypes.string,
};

let uid = 0;
class TipComponent extends Component {
    constructor(props) {
        super(props);
        uid += 1;

        this.dropRef = React.createRef();
        this.arrowRef = React.createRef();

        this.state = {
            uid,
            show: false,
        };
    }

    static layers = Tip.layers;
    static placements = Tip.placements;
    static themes = Tip.themes;
    static defaultProps = Tip.defaultProps;
    static propTypes = Tip.propTypes;

    componentDidMount() {
        this.activatorRef = ReactDOM.findDOMNode(this);
        window.addEventListener('resize', this.updateTipPosition);

        if (this.props.show) {
            this.setState({ show: true });
            this.props.context.setShow(this.state.uid);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { showedId, setShow } = this.props.context;
        if (this.state.show && !prevState.show) {
            setInitialCSSMetrics(this.dropRef.current);
        }
        if (
            showedId === prevProps.context.showedId &&
            this.state.show === prevState.show &&
            this.props.show &&
            showedId !== this.state.uid
        ) {
            this.setState({
                show: true,
            });
            setShow(this.state.uid);
        }

        if (showedId !== prevProps.context.showedId && this.state.show && showedId !== this.state.uid) {
            this.props.onExternalClose(false);
        } else if (this.props.show !== prevProps.show) {
            this.setState({
                show: this.props.show,
            });
        }

        this.updateTipPosition();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateTipPosition);
    }

    getHost() {
        return this.props.host || this.activatorRef.parentNode;
    }

    updateTipPosition = () => {
        if (this.state.show && this.dropRef.current) {
            updatePosition({
                placementSequence: PLACEMENT_TIP_SEQUENCE,
                activatorElement: this.activatorRef,
                dropElement: this.dropRef.current,
                host: this.getHost(),
                placement: this.props.placement,
                classNames: [...BASE_CLASS_NAMES, LAYER_CLASSES[this.props.layer]],
                arrowSize: ARROW_SIZE,
                arrow: this.arrowRef.current,
                behavior: {
                    placementOffset: DISTANCE + ARROW_SIZE,
                    alignToActivatorBorders: false,
                },
            });

            this.dropRef.current.classList.add(getThemeClass(this.props.theme));
        }
    };

    renderTip() {
        const {
            children,
            placement,
            theme,
            context,
            host,
            show,
            layer,
            render,
            dataQa,
            onExternalClose,
            ...tooltipProps
        } = this.props;

        return (
            <CSSTransition
                in={this.state.show}
                timeout={{
                    enter: ANIMATION_TIMEOUT_MS,
                    exit: 0,
                }}
                unmountOnExit
                classNames={{
                    enterActive: 'bloko-drop_active-enter',
                    enterDone: 'bloko-drop_done-enter',
                }}
            >
                <div
                    className={[...BASE_CLASS_NAMES, LAYER_CLASSES[layer]].join(' ')}
                    ref={this.dropRef}
                    {...tooltipProps}
                >
                    <div data-qa={dataQa}>{render()}</div>
                    <div className="bloko-drop__arrow" ref={this.arrowRef} />
                </div>
            </CSSTransition>
        );
    }

    render() {
        return (
            <Fragment>
                {this.props.children}
                {this.props.host ? ReactDOM.createPortal(this.renderTip(), this.props.host) : this.renderTip()}
            </Fragment>
        );
    }
}

export default Tip;
